import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { nextHolder, tablePlayers } from '@continuum/show-core';
import type { GuestIntent, TableGame as TableGameState } from '@continuum/show-core';
import type { WireState, You } from '../room/client.js';
import { mono, sans } from '../ui/atoms.js';
import type { Lang } from '../ui/text.js';
import { useDragDismiss } from '../ui/drag.js';
import { TopBanner } from '../ui/TopBanner.js';
import { PRIORITY, useBanner, useCoverCount } from '../ui/OverlayHost.js';

/**
 * The phone's half of the table's own games (show-core `tablegame.ts`). The table projector shows
 * the game; the phone is the hand.
 *
 * - Pass it on: the guest holding the light gets one big button; everyone else sees where it is.
 * - Plant: one tap each, and something grows at your place on the table.
 *
 * On the table itself a hand put down at the guest's place plays (Marek, 2026-10-06: the camera counts
 * a hand on the table, never one waved above it), so the words say "put your hand on the table".
 *
 * It is a small sheet that peeks up under the screen, in the page flow, so it never covers
 * anything. Pull it down, or open a game or a sheet, and it becomes a bar at the top that
 * still carries the button. Tap the bar and the card comes back.
 */
const COPY = {
  en: {
    passTitle: 'PASS IT ON',
    passYours: 'The light is at your place — put your hand on the table there to pass it on. +100 points.',
    passYoursShort: 'The light is with you — hand on the table!',
    passButton: 'PASS IT ON →',
    passTo: (name: string) => `next: ${name}`,
    passWith: (name: string) => `The light is with ${name}. Watch the table.`,
    passWithShort: (name: string) => `The light is with ${name}`,
    laps: (n: number) => (n === 1 ? 'once round the table' : `${n} times round the table`),
    plantTitle: 'PLANT SOMETHING',
    plantBody: 'Put your hand on the table at your place — or tap PLANT. +250 points.',
    plantButton: 'PLANT',
    planted: 'Planted. Look down.',
    garden: (n: number, of: number) => `${n} of ${of} have planted`,
    out: 'You’re voted out, so you sit this one out.',
  },
  no: {
    passTitle: 'SEND DET VIDERE',
    passYours: 'Lyset er ved plassen din – legg hånden på bordet der for å sende det videre. +100 poeng.',
    passYoursShort: 'Lyset er hos deg – hånden på bordet!',
    passButton: 'SEND DET VIDERE →',
    passTo: (name: string) => `neste: ${name}`,
    passWith: (name: string) => `Lyset er hos ${name}. Se på bordet.`,
    passWithShort: (name: string) => `Lyset er hos ${name}`,
    laps: (n: number) => (n === 1 ? 'én runde rundt bordet' : `${n} runder rundt bordet`),
    plantTitle: 'PLANT NOE',
    plantBody: 'Legg hånden på bordet ved plassen din – eller trykk PLANT. +250 poeng.',
    plantButton: 'PLANT',
    planted: 'Plantet. Se ned.',
    garden: (n: number, of: number) => `${n} av ${of} har plantet`,
    out: 'Du er stemt ut, så du står over denne.',
  },
} as const;

const GOLD = '#f2b04b';

function useNow(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

export function TableGame({
  lang,
  game,
  state,
  you,
  send,
  gameOpen,
  onOpenCard,
}: {
  lang: Lang;
  game: TableGameState;
  state: WireState;
  you: You;
  send: (intent: GuestIntent) => void;
  /** A game screen is open: the card steps aside to the top bar. */
  gameOpen: boolean;
  /** The bar was tapped: close whatever game is open, so the card has room. */
  onOpenCard: () => void;
}) {
  const c = COPY[lang];
  const now = useNow(1_000);
  const [collapsed, setCollapsed] = useState(false);
  // The bar can be flicked away; it comes back when the moment changes (the light reaches you).
  const [barAway, setBarAway] = useState<string | null>(null);
  const covered = useCoverCount() > 0;
  const live = now < game.endsAt;
  const asBar = live && (collapsed || gameOpen || covered);

  const nameOf = (seat: number | null) =>
    seat === null ? '' : (state.seats.find((s) => s.seat === seat)?.callsign ?? `#${seat}`);
  // The guests at the table, the same list the room, the TV and the cockpit count (show-core).
  const players = tablePlayers(state.seats);
  const playing = players.includes(you.seat);

  let title: string;
  let line: string;
  let short: string;
  let action: { label: string; intent: GuestIntent } | null = null;
  let small: string[] = [];
  if (game.kind === 'pass') {
    const mine = game.holder === you.seat;
    // Who it goes to is drawn by the room; every screen works out the same guest from the game.
    const next = mine ? nextHolder(game, players) : null;
    title = c.passTitle;
    line = mine ? c.passYours : c.passWith(nameOf(game.holder));
    short = mine ? c.passYoursShort : c.passWithShort(nameOf(game.holder));
    if (mine) action = { label: c.passButton, intent: { type: 'tablePass' } };
    if (mine && next !== null) small.push(c.passTo(nameOf(next)));
    if (game.laps > 0) small.push(c.laps(game.laps));
  } else {
    const done = game.planted[String(you.seat)] !== undefined;
    title = c.plantTitle;
    line = done ? c.planted : playing ? c.plantBody : c.out;
    short = line;
    if (!done && playing) action = { label: c.plantButton, intent: { type: 'tablePlant' } };
    small = [c.garden(players.filter((seat) => game.planted[String(seat)] !== undefined).length, players.length)];
  }

  const fire = (intent: GuestIntent) => {
    navigator.vibrate?.(20);
    send(intent);
  };

  const moment = game.kind === 'pass' ? `pass:${game.holder === you.seat}` : `plant:${game.planted[String(you.seat)] !== undefined}`;
  const bar =
    asBar && barAway !== moment ? (
      <TopBanner
        compact
        tone={{ fg: GOLD, bg: '#1d1305', border: GOLD }}
        tapToDismiss={false}
        onDismiss={() => setBarAway(moment)}
        onOpen={() => {
          setCollapsed(false);
          onOpenCard();
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: sans, fontSize: 16, fontWeight: 700, letterSpacing: 2 }}>{title}</div>
            <div style={{ fontFamily: mono, fontSize: 12, color: '#f6ead6', lineHeight: 1.35 }}>{short}</div>
          </div>
          {action ? (
            <button
              onClick={() => action && fire(action.intent)}
              style={{
                flex: 'none',
                minHeight: 48,
                padding: '0 14px',
                borderRadius: 12,
                border: 'none',
                background: GOLD,
                color: '#241604',
                fontFamily: sans,
                fontSize: 15,
                fontWeight: 700,
                letterSpacing: 1.5,
              }}
            >
              {action.label}
            </button>
          ) : (
            <span aria-hidden="true" style={{ flex: 'none', fontSize: 20 }}>
              ›
            </span>
          )}
        </div>
      </TopBanner>
    ) : null;
  useBanner(`table:${game.kind}`, PRIORITY.table, bar);

  if (!live || asBar) return null;
  return (
    <PeekCard onCollapse={() => setCollapsed(true)}>
      <div style={{ fontFamily: sans, fontSize: 20, fontWeight: 700, letterSpacing: 3, color: GOLD }}>{title}</div>
      <div style={{ marginTop: 4, fontFamily: sans, fontSize: 17, lineHeight: 1.3, color: '#f6ead6' }}>{line}</div>
      {action && (
        <button
          onClick={() => action && fire(action.intent)}
          style={{
            marginTop: 10,
            width: '100%',
            minHeight: 60,
            borderRadius: 14,
            border: 'none',
            background: GOLD,
            color: '#241604',
            fontFamily: sans,
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: 3,
          }}
        >
          {action.label}
        </button>
      )}
      {small.map((text) => (
        <div key={text} style={{ marginTop: 6, fontFamily: mono, fontSize: 12, color: '#d8b98a' }}>
          {text}
        </div>
      ))}
    </PeekCard>
  );
}

/** The card itself: a grabber on top, and a pull down makes it a bar. */
function PeekCard({ children, onCollapse }: { children: ReactNode; onCollapse: () => void }) {
  const drag = useDragDismiss<HTMLDivElement>({ direction: 'down', onDismiss: onCollapse, distance: 50, flick: 0.4 });
  const style: CSSProperties = {
    position: 'relative',
    padding: '0 16px 14px',
    background: '#181006',
    borderTop: '1px solid rgba(242,176,75,.7)',
    borderRadius: '18px 18px 0 0',
    boxShadow: '0 -8px 30px rgba(242,176,75,.18)',
    textAlign: 'center',
    touchAction: 'none',
  };
  return (
    <div
      ref={(el) => {
        drag.ref.current = el;
        drag.handleRef(el);
      }}
      {...drag.bind}
      className="cc-sheet"
      role="region"
      style={style}
    >
      <div style={{ height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span aria-hidden="true" style={{ width: 36, height: 5, borderRadius: 3, background: GOLD, opacity: 0.55 }} />
      </div>
      {children}
    </div>
  );
}
